/**
 * CancelFlowModal — V2 Premium Experience (V2.0 Phase 5, Cluster A).
 *
 * Retention → Reason cancel flow modals.
 *
 * Step 1: Retention offer sheet (pause / switch annual / contact / keep / continue)
 * Step 2: Reason picker + optional textarea + submit
 *
 * Renders in a portal-like overlay over /account/billing. On confirm,
 * calls POST /api/payments/cancel-subscription (which persists the reason
 * AND sets Stripe cancel_at_period_end=true).
 */
import { useState } from "react";
import { X, ArrowLeft, Loader2, Pause, RefreshCcw, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";

const REASONS = [
  { code: "too_expensive", label: "Too expensive" },
  { code: "not_using_it_enough", label: "Not using it enough" },
  { code: "didnt_achieve_goal", label: "Didn't achieve my goal" },
  { code: "technical_issues", label: "Technical issues" },
  { code: "missing_features", label: "Missing features" },
  { code: "found_another_solution", label: "Found another solution" },
  { code: "other", label: "Other" },
];

function ModalShell({ children, onClose, testid }) {
  return (
    <div
      data-testid={testid}
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-v2-2xl bg-v2-surface border border-v2-border shadow-v2-modal p-6 max-h-[90vh] overflow-y-auto"
      >
        {children}
      </div>
    </div>
  );
}

function OfferButton({ icon: Icon, label, hint, onClick, testid, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`w-full text-left rounded-v2-lg border p-4 transition-all ${
        danger
          ? "border-v2-danger/40 hover:border-v2-danger bg-v2-dangerSoft/40 hover:bg-v2-dangerSoft"
          : "border-v2-border hover:border-v2-accent hover:bg-v2-accentSoft/50 bg-v2-surface"
      }`}
    >
      <div className="flex items-start gap-3">
        {Icon && (
          <div
            className={`w-9 h-9 rounded-v2-md flex items-center justify-center shrink-0 ${
              danger ? "bg-v2-dangerSoft" : "bg-v2-accentSoft"
            }`}
          >
            <Icon className={`w-4 h-4 ${danger ? "text-v2-danger" : "text-v2-accent"}`} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className={`text-v2-body-md ${danger ? "text-v2-danger" : "text-v2-text"}`}>{label}</div>
          {hint && (
            <div className="text-v2-body-sm text-v2-textMuted mt-1 leading-snug">{hint}</div>
          )}
        </div>
      </div>
    </button>
  );
}

export default function CancelFlowModal({ isOpen, onClose, onDone }) {
  const [step, setStep] = useState(1);
  const [reasonCode, setReasonCode] = useState(null);
  const [reasonText, setReasonText] = useState("");
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  async function pauseInstead() {
    try {
      setSubmitting(true);
      await api.post("/payments/pause-subscription", { duration_days: 30 });
      toast.success("Your subscription is paused for 30 days.");
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function openPortalForAnnualSwap() {
    try {
      setSubmitting(true);
      const { data } = await api.post("/payments/customer-portal-session", {
        flow: "subscription_update",
      });
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function submitCancel() {
    if (!reasonCode) return;
    if (reasonCode === "other" && !reasonText.trim()) {
      toast.error("Please tell us what happened before you cancel.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/payments/cancel-subscription", {
        reason_code: reasonCode,
        reason_text: reasonText.trim() || null,
        additional_notes: additionalNotes.trim() || null,
      });
      toast.success("Your subscription is set to cancel at the end of the current period.");
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (step === 1) {
    return (
      <ModalShell onClose={onClose} testid="cancel-flow-step1-modal">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3
              className="text-v2-heading-lg text-v2-text"
              style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
            >
              We're sorry to see you go.
            </h3>
            <p className="text-v2-body-sm text-v2-textMuted mt-1">
              Before you cancel, would one of these options help?
            </p>
          </div>
          <button
            data-testid="cancel-flow-step1-close"
            onClick={onClose}
            className="text-v2-textMuted hover:text-v2-text transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-3">
          <OfferButton
            icon={Pause}
            label="Pause my membership for 30 days"
            hint="You won't be charged during the pause. Resume any time."
            onClick={pauseInstead}
            testid="cancel-flow-pause-btn"
          />
          <OfferButton
            icon={RefreshCcw}
            label="Switch to annual billing (save 17%)"
            hint="Move to yearly and lock in a 17% discount."
            onClick={openPortalForAnnualSwap}
            testid="cancel-flow-switch-annual-btn"
          />
          <OfferButton
            icon={MessageCircle}
            label="Contact support"
            hint="We'll help you make Informed OS work for you."
            onClick={() => { window.location.href = "mailto:support@informedos.com?subject=Need%20help%20before%20canceling"; }}
            testid="cancel-flow-contact-btn"
          />
          <div className="pt-2 flex flex-col sm:flex-row items-stretch gap-2">
            <button
              data-testid="cancel-flow-keep-btn"
              onClick={onClose}
              className="flex-1 rounded-v2-md border border-v2-border px-4 py-2.5 text-v2-body-sm text-v2-text hover:bg-v2-surfaceSubtle transition-colors"
            >
              Keep my subscription
            </button>
            <button
              data-testid="cancel-flow-continue-btn"
              onClick={() => setStep(2)}
              className="flex-1 rounded-v2-md px-4 py-2.5 text-v2-body-sm bg-v2-dangerSoft text-v2-danger hover:opacity-80 transition-opacity"
            >
              Continue with cancellation
            </button>
          </div>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell onClose={onClose} testid="cancel-flow-step2-modal">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3
            className="text-v2-heading-lg text-v2-text"
            style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
          >
            Help us improve.
          </h3>
          <p className="text-v2-body-sm text-v2-textMuted mt-1">Why are you canceling?</p>
        </div>
        <button
          data-testid="cancel-flow-step2-close"
          onClick={onClose}
          className="text-v2-textMuted hover:text-v2-text transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="space-y-2 mb-5">
        {REASONS.map((r) => {
          const selected = reasonCode === r.code;
          return (
            <label
              key={r.code}
              data-testid={`cancel-flow-reason-${r.code}`}
              className={`flex items-center gap-3 text-v2-body-sm p-3 rounded-v2-md cursor-pointer border transition-colors ${
                selected
                  ? "bg-v2-accentSoft border-v2-accent"
                  : "bg-v2-surface border-v2-border hover:border-v2-borderStrong"
              }`}
            >
              <input
                type="radio"
                name="reason"
                value={r.code}
                checked={selected}
                onChange={() => setReasonCode(r.code)}
                data-testid={`cancel-flow-reason-radio-${r.code}`}
                className="accent-v2-accent"
              />
              <span className="text-v2-text">{r.label}</span>
            </label>
          );
        })}
      </div>
      {reasonCode === "other" && (
        <div className="mb-4">
          <div className="text-v2-caption uppercase tracking-[0.16em] text-v2-textMuted mb-1.5">
            Tell us more (required)
          </div>
          <textarea
            data-testid="cancel-flow-reason-text"
            value={reasonText}
            onChange={(e) => setReasonText(e.target.value.slice(0, 500))}
            rows={3}
            maxLength={500}
            className="w-full bg-v2-surface border border-v2-border rounded-v2-md px-3 py-2 text-v2-body-sm text-v2-text focus:outline-none focus:border-v2-accent transition-colors"
            placeholder="What happened?"
          />
          <div className="text-v2-caption text-v2-textSubtle text-right mt-1">
            {reasonText.length}/500
          </div>
        </div>
      )}
      {reasonCode && reasonCode !== "other" && (
        <div className="mb-4">
          <div className="text-v2-caption uppercase tracking-[0.16em] text-v2-textMuted mb-1.5">
            Anything else you'd like us to know? (optional)
          </div>
          <textarea
            data-testid="cancel-flow-additional-notes"
            value={additionalNotes}
            onChange={(e) => setAdditionalNotes(e.target.value.slice(0, 500))}
            rows={2}
            maxLength={500}
            className="w-full bg-v2-surface border border-v2-border rounded-v2-md px-3 py-2 text-v2-body-sm text-v2-text focus:outline-none focus:border-v2-accent transition-colors"
          />
        </div>
      )}
      <div className="flex items-stretch gap-2">
        <button
          data-testid="cancel-flow-back-btn"
          onClick={() => setStep(1)}
          className="rounded-v2-md border border-v2-border px-4 py-2.5 text-v2-body-sm text-v2-textMuted hover:text-v2-text hover:bg-v2-surfaceSubtle transition-colors flex items-center gap-1.5"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <button
          data-testid="cancel-flow-submit-btn"
          onClick={submitCancel}
          disabled={!reasonCode || submitting || (reasonCode === "other" && !reasonText.trim())}
          className="flex-1 rounded-v2-md px-4 py-2.5 text-v2-body-sm font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2 bg-v2-danger text-white hover:bg-v2-danger/90 transition-colors"
        >
          {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Submit and cancel
        </button>
      </div>
    </ModalShell>
  );
}
